import { DefaultMapping } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView } from '@synergy/components-palo';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as AttributeActions from '../../../../common/actions/attributes-actions';
import { Attribute } from '../../../../common/models/attribute';
import { MappedValues } from '../../../../common/components/mapped-values';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface AttributesMappingOverviewProps {
    onEdit: (item: DefaultMapping<Attribute>) => void;
}

const columns: PaloColumn<DefaultMapping<Attribute>>[] = [
    {
        key: 'name',
        type: 'name',
        head: <FormattedMessage id="app.table.column.name" />,
        cell: attribute => attribute.name,
    },
    {
        key: 'locale',
        type: 'classification',
        head: <FormattedMessage id="app.table.column.locale" />,
        cell: attribute => attribute.locale.toUpperCase(),
    },
    {
        key: 'mapping',
        type: 'name',
        head: <FormattedMessage id="app.table.column.mapping" />,
        cell: attribute => <MappedValues values={attribute.defaultMapping} />,
    },
];

export const AttributesMappingOverview = (props: AttributesMappingOverviewProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useThunkDispatch();
    const { defaultMappings: attributes } = useAppSelector(state => state.attributes);

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    useEffect(() => {
        dispatch(AttributeActions.fetchAttributesMapping(searchParams));
    }, [dispatch, searchParams]);

    return (
        <PaloListView<DefaultMapping<Attribute>, number>
            embedded={true}
            columns={columns}
            items={attributes.items}
            getRowId={attribute => attribute.id}
            emptyLabel="Keine Attribute."
            onRowClick={props.onEdit}
            rowActions={attribute => [
                { label: <FormattedMessage id="app.common.edit" />, onClick: () => props.onEdit(attribute) },
            ]}
            pagination={{
                count: attributes.total,
                page: Number(searchParams.get('page') ?? 1),
                limit: Number(searchParams.get('limit') ?? 50),
                onPageChange: page => updateParams({ page: String(page) }),
                onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
            }}
        />
    );
};
